import { ref } from 'vue';

const LONG_PRESS_DURATION = 500;

export interface UseMapContextMenuOptions {
  viewport: HTMLElement;
  onOpen: (event: MouseEvent) => void;
}

export interface UseMapContextMenu {
  setup: () => void;
  cleanup: () => void;
}

export function useMapContextMenu(options: UseMapContextMenuOptions): UseMapContextMenu {
  const longPressTimer = ref<ReturnType<typeof setTimeout> | null>(null);

  const clearLongPress = (): void => {
    if (longPressTimer.value !== null) {
      clearTimeout(longPressTimer.value);
      longPressTimer.value = null;
    }
  };

  const handleContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    options.onOpen(event);
  };

  const handleTouchStart = (event: TouchEvent): void => {
    if (event.touches.length !== 1) {
      return;
    }

    const touch = event.touches[0];

    longPressTimer.value = setTimeout(() => {
      // Simulate a mouse event at the touch position
      const mouseEvent = new MouseEvent('contextmenu', {
        clientX: touch.clientX,
        clientY: touch.clientY,
        bubbles: true,
        cancelable: true,
      });
      handleContextMenu(mouseEvent);
    }, LONG_PRESS_DURATION);
  };

  const setup = (): void => {
    const { viewport } = options;
    viewport.addEventListener('contextmenu', handleContextMenu);
    viewport.addEventListener('touchstart', handleTouchStart);
    viewport.addEventListener('touchend', clearLongPress);
    viewport.addEventListener('touchmove', clearLongPress);
  };

  const cleanup = (): void => {
    const { viewport } = options;
    viewport.removeEventListener('contextmenu', handleContextMenu);
    viewport.removeEventListener('touchstart', handleTouchStart);
    viewport.removeEventListener('touchend', clearLongPress);
    viewport.removeEventListener('touchmove', clearLongPress);
    clearLongPress();
  };

  return {
    setup,
    cleanup,
  };
}
